import { Component, OnInit, Output, EventEmitter, Input, forwardRef, HostListener, ElementRef, ViewChild, Renderer2, Inject, OnDestroy } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CalendarDay } from '../../models/calender.model';

export enum CalendarView {
  year = 'year',
  month = 'month',
  day = 'day'
}

@Component({
    selector: 'hmt-custom-date-picker',
    templateUrl: './custom-date-picker.component.html',
    styleUrls: ['./custom-date-picker.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => CustomDatePickerComponent),
            multi: true
        }
    ],
    standalone: false
})
export class CustomDatePickerComponent implements OnInit, ControlValueAccessor, OnDestroy {

  @Input() dateFormat = 'dd-MM-yyyy';
  @Input() startDate: Date = new Date();
  @Input() endDate: Date = new Date();
  @Input() minDate: Date = null;
  @Input() maxDate: Date = null;
  @Input() singleDateSelection = false;
  @Input() enableTimeSelection = true;
  @Input() positionPopupAbove = false; // When true, positions popup above input field
  @Output() dateRangeSelected = new EventEmitter<{ startDate: Date; endDate: Date }>();

  @ViewChild('calendarPopup', { static: false }) calendarPopup: ElementRef;
  @ViewChild('dateRangeDisplay', { static: false }) dateRangeDisplay: ElementRef;

  private calendarPopupElement: HTMLElement;
  private originalParent: HTMLElement;
  private scrollListeners: (() => void)[] = [];
  private animationFrameId: number = null;

  // Calendar view state
  currentView: CalendarView = CalendarView.day;
  calendarView = CalendarView; // Expose enum to template

  currentMonth: number;
  currentYear: number;
  currentDate: Date = new Date();
  calendarDays: CalendarDay[] = [];
  // Monday-first week labels to match design system
  weekDays = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
  months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  shortMonths = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  availableYears: number[] = [];
  isOpen = false;
  selectionStart: Date = null;
  selectionEnd: Date = null;
  isSelecting = false;
  isExpectingFromDate = true;
  isDisabled = false;

  // Time selection properties
  fromTime = { hour: 9, minute: 0 };
  toTime = { hour: 17, minute: 0 };
  activeTimeSelector: 'from' | 'to' = 'from';
  hours: number[] = Array.from({ length: 24 }, (_, i) => i);

  // 12-hour toggle state
  @Input() use12Hours = true;
  amPm: { from: 'AM' | 'PM'; to: 'AM' | 'PM' } = { from: 'AM', to: 'PM' };

  // For year/month selection
  yearRange: { start: number; end: number } = { start: 2010, end: 2040 };

  pasteSuccess = false;

  onChange: any = () => {};
  onTouched: any = () => {};

  // backups for Cancel
  private backupSelectionStart: Date = null;
  private backupSelectionEnd: Date = null;
  private backupFromTime: { hour: number; minute: number } = null;
  private backupToTime: { hour: number; minute: number } = null;
  private backupAmPm: { from: 'AM' | 'PM'; to: 'AM' | 'PM' } = null;

  constructor(
    private elementRef: ElementRef,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.initializeYearRange();
  }

  @HostListener('document:click', ['$event'])
  clickOutside(event: Event): void {
    if (!this.isOpen) {
      return;
    }

    const target = event.target as HTMLElement;

    // Check if click is inside the calendar popup (which may be attached to body)
    if (this.calendarPopupElement && this.calendarPopupElement.contains(target)) {
      return;
    }

    // Check if click is inside the date-range-display trigger element
    if (this.dateRangeDisplay && this.dateRangeDisplay.nativeElement.contains(target)) {
      return;
    }

    // Click is outside, close the calendar
    // Use timeout to ensure DOM operations complete properly
    setTimeout(() => {
      this.isOpen = false;
      this.currentView = CalendarView.day;
      this.restoreCalendarPopup();
    }, 0);
  }

  @HostListener('window:scroll', ['$event'])
  onWindowScroll(event: Event): void {
    if (this.isOpen && this.calendarPopupElement) {
      this.schedulePositionUpdate();
    }
  }

  @HostListener('window:resize', ['$event'])
  onWindowResize(event: Event): void {
    if (this.isOpen && this.calendarPopupElement) {
      this.schedulePositionUpdate();
    }
  }

  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pastedText = event.clipboardData?.getData('text/plain')?.trim();

    if (pastedText) {
      const parsedDate = this.parseDateTimeFromText(pastedText);
      if (parsedDate) {
        this.setDateFromParsedValue(parsedDate);
        this.emitSelection();

        // Show success feedback
        this.pasteSuccess = true;
        setTimeout(() => {
          this.pasteSuccess = false;
        }, 1000);
      }
    }
  }

  private schedulePositionUpdate(): void {
    // Cancel any pending animation frame to prevent delayed updates
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
    
    // Update position immediately for smooth, responsive movement
    // No delay - updates happen synchronously with scroll events
    this.updateCalendarPopupPosition();
  }

  ngOnInit(): void {
    this.currentMonth = new Date().getMonth();
    this.currentYear = new Date().getFullYear();
    this.generateCalendar();

    if (this.startDate && this.endDate) {
      // Normalize selections to start of day to ensure template equality checks work
      this.selectionStart = this.getStartOfDay(this.startDate);
      this.selectionEnd = this.getStartOfDay(this.endDate);

      // Initialize time from existing dates or set defaults
      if (this.enableTimeSelection) {
        // If dates already have meaningful times, use them
        if (this.startDate.getHours() !== 0 || this.startDate.getMinutes() !== 0 ||
            this.endDate.getHours() !== 0 || this.endDate.getMinutes() !== 0) {
          this.fromTime = {
            hour: this.startDate.getHours(),
            minute: this.startDate.getMinutes()
          };
          this.toTime = {
            hour: this.endDate.getHours(),
            minute: this.endDate.getMinutes()
          };
          this.syncAmPmFromTimes();
        } else {
          // Set default times based on selection type
          if (this.singleDateSelection || this.isSameDate(this.startDate, this.endDate)) {
            this.fromTime = { hour: 0, minute: 0 };
            this.toTime = { hour: 23, minute: 59 };
          } else {
            this.fromTime = { hour: 0, minute: 0 };
            this.toTime = { hour: 23, minute: 59 };
          }
          this.syncAmPmFromTimes();
        }
      }

      this.updateDateDisplay();
    }
  }

  writeValue(value: any): void {
    if (value) {
      if (this.singleDateSelection) {
        this.startDate = new Date(value);
        this.endDate = new Date(value);
      } else if (Array.isArray(value) && value.length === 2) {
        this.startDate = new Date(value[0]);
        this.endDate = new Date(value[1]);
      } else if (value.startDate && value.endDate) {
        this.startDate = new Date(value.startDate);
        this.endDate = new Date(value.endDate);
      }

      // Normalize selections to start of day to ensure template equality checks work
      this.selectionStart = this.getStartOfDay(this.startDate);
      this.selectionEnd = this.getStartOfDay(this.endDate);

      // Update time selectors if time selection is enabled
      if (this.enableTimeSelection) {
        this.fromTime = {
          hour: this.startDate.getHours(),
          minute: this.startDate.getMinutes()
        };
        this.toTime = {
          hour: this.endDate.getHours(),
          minute: this.endDate.getMinutes()
        };
        this.syncAmPmFromTimes();
      }

      this.updateDateDisplay();
      this.generateCalendar();
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  toggleCalendar(event: MouseEvent): void {
    if (this.isDisabled) {
      return;
    }
    event.stopPropagation();

    // If calendar is already open, close it instead of toggling
    if (this.isOpen) {
      this.isOpen = false;
      this.currentView = CalendarView.day;
      this.restoreCalendarPopup();
      return;
    }

    this.isOpen = true;
    if (this.isOpen) {
      this.currentView = CalendarView.day; // Ensure we start in day view

      // Navigate to the month/year of the selected date
      if (this.selectionStart) {
        this.currentMonth = this.selectionStart.getMonth();
        this.currentYear = this.selectionStart.getFullYear();
      } else if (this.startDate) {
        // Fallback to startDate if no selection
        this.currentMonth = this.startDate.getMonth();
        this.currentYear = this.startDate.getFullYear();
      } else {
        // Default to current date if no selection or start date
        const now = new Date();
        this.currentMonth = now.getMonth();
        this.currentYear = now.getFullYear();
      }

      this.generateCalendar();
      // backup current selection to support Cancel action
      this.backupSelectionStart = this.selectionStart ? new Date(this.selectionStart) : null;
      this.backupSelectionEnd = this.selectionEnd ? new Date(this.selectionEnd) : null;
      this.backupFromTime = { ...this.fromTime };
      this.backupToTime = { ...this.toTime };
      this.backupAmPm = { ...this.amPm } as { from: 'AM' | 'PM'; to: 'AM' | 'PM' };

      // Move calendar popup to body after view updates
      setTimeout(() => {
        this.moveCalendarPopupToBody();
      }, 0);
    } else {
      // Use timeout to ensure DOM operations complete before Angular removes the element
      setTimeout(() => {
        this.restoreCalendarPopup();
      }, 0);
    }
  }

  private updateCalendarPopupPosition(): void {
    if (!this.calendarPopupElement || !this.dateRangeDisplay) {
      return;
    }

    const dateRangeDisplayElement = this.dateRangeDisplay.nativeElement;

    // Find the visible date-range-text element (only one is visible at a time due to *ngIf)
    const dateRangeTextElement = dateRangeDisplayElement.querySelector('.date-range-text') as HTMLElement;
    
    // Use date-range-text if found, otherwise fallback to date-range-display
    const targetElement = dateRangeTextElement || dateRangeDisplayElement;

    // Calculate position based on the target element
    const rect = targetElement.getBoundingClientRect();
    const popupWidth = 328;
    const popupHeight = 500; // Approximate height of calendar popup
    const margin = 5;

    // Calculate available space above and below
    const spaceAbove = rect.top - margin;
    const spaceBelow = window.innerHeight - rect.bottom - margin;

    let top: number;
    let maxHeight: number;

    // Only position above if explicitly requested (for relative date selector)
    if (this.positionPopupAbove) {
      // Position above the input field
      top = rect.top - popupHeight - margin;
      
      // If not enough space above, position below instead
      if (top < margin || spaceAbove < 200) {
        top = rect.bottom + margin;
        maxHeight = Math.max(300, spaceBelow);
      } else {
        maxHeight = Math.max(300, spaceAbove);
      }
    } else {
      // Default behavior: position below (original behavior)
      top = rect.bottom + margin;
      maxHeight = Math.max(300, spaceBelow);
    }
    
    // Align left edge with the date-range-text left edge
    let left = rect.left;

    // Adjust if popup would go off right edge (shift left)
    if (left + popupWidth > window.innerWidth) {
      left = window.innerWidth - popupWidth - margin;
    }

    // Adjust if popup would go off left edge (shift right)
    if (left < margin) {
      left = margin;
    }

    // Update position styles
    this.renderer.setStyle(this.calendarPopupElement, 'top', `${top}px`);
    this.renderer.setStyle(this.calendarPopupElement, 'left', `${left}px`);
    this.renderer.setStyle(this.calendarPopupElement, 'max-height', `${maxHeight}px`);
  }


  private moveCalendarPopupToBody(): void {
    if (!this.calendarPopup || !this.dateRangeDisplay) {
      return;
    }

    this.calendarPopupElement = this.calendarPopup.nativeElement;
    const dateRangeDisplayElement = this.dateRangeDisplay.nativeElement;

    // Store original parent
    this.originalParent = this.calendarPopupElement.parentElement;

    // Set initial position styles
    this.renderer.setStyle(this.calendarPopupElement, 'position', 'fixed');
    this.renderer.setStyle(this.calendarPopupElement, 'z-index', '999999');
    this.renderer.setStyle(this.calendarPopupElement, 'margin-top', '0');
    this.renderer.setStyle(this.calendarPopupElement, 'overflow-y', 'auto');

    // Calculate and set initial position
    this.updateCalendarPopupPosition();

    // Append to body
    this.renderer.appendChild(this.document.body, this.calendarPopupElement);

    // Find all scrollable parent containers and add scroll listeners
    this.addScrollListenersToParents(dateRangeDisplayElement);
  }

  private addScrollListenersToParents(element: HTMLElement): void {
    // Find all scrollable parent elements
    let current: HTMLElement | null = element;
    const scrollableParents: HTMLElement[] = [];

    while (current && current !== this.document.body) {
      const style = window.getComputedStyle(current);
      const isScrollable = 
        style.overflow === 'auto' || 
        style.overflow === 'scroll' || 
        style.overflowY === 'auto' || 
        style.overflowY === 'scroll' ||
        style.overflowX === 'auto' || 
        style.overflowX === 'scroll';

      if (isScrollable && (current.scrollHeight > current.clientHeight || current.scrollWidth > current.clientWidth)) {
        scrollableParents.push(current);
      }
      current = current.parentElement;
    }

    // Add scroll listeners to all scrollable parents
    scrollableParents.forEach(parent => {
      const listener = this.renderer.listen(parent, 'scroll', () => {
        if (this.isOpen && this.calendarPopupElement) {
          this.schedulePositionUpdate();
        }
      });
      this.scrollListeners.push(listener);
    });
  }

  private restoreCalendarPopup(): void {
    // Cancel any pending animation frame
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }

    // Remove all scroll listeners
    this.scrollListeners.forEach(listener => listener());
    this.scrollListeners = [];

    if (this.calendarPopupElement && this.originalParent) {
      // Restore original styles
      this.renderer.removeStyle(this.calendarPopupElement, 'position');
      this.renderer.removeStyle(this.calendarPopupElement, 'top');
      this.renderer.removeStyle(this.calendarPopupElement, 'left');
      this.renderer.removeStyle(this.calendarPopupElement, 'z-index');
      this.renderer.removeStyle(this.calendarPopupElement, 'max-height');
      this.renderer.removeStyle(this.calendarPopupElement, 'overflow-y');
      this.renderer.setStyle(this.calendarPopupElement, 'margin-top', '5px');

      // Restore to original parent
      this.renderer.appendChild(this.originalParent, this.calendarPopupElement);
      this.calendarPopupElement = null;
      this.originalParent = null;
    }
  }

  ngOnDestroy(): void {
    // Clean up: restore calendar popup if still attached to body
    this.restoreCalendarPopup();
  }

  formatDate(date: Date): string {
    if (!date) {
      return '';
    }

    // Build base date string honoring simple tokens in dateFormat (supports dd, MM, yyyy)
    const dd = date.getDate().toString().padStart(2, '0');
    const MM = (date.getMonth() + 1).toString().padStart(2, '0');
    const yyyy = date.getFullYear().toString();

    let baseDate: string;
    if (this.dateFormat && /dd/.test(this.dateFormat) && /MM/.test(this.dateFormat) && /yyyy/.test(this.dateFormat)) {
      baseDate = this.dateFormat
        .replace(/dd/g, dd)
        .replace(/MM/g, MM)
        .replace(/yyyy/g, yyyy);
    } else {
      // Fallback to "dd MMM yyyy"
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                          'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const month = monthNames[date.getMonth()];
      baseDate = `${dd} ${month} ${yyyy}`;
    }

    if (this.enableTimeSelection) {
      // Use lowercase am/pm and non-padded hour for 12h format (e.g., "9:00 am")
      const hours24 = date.getHours();
      const minutes = date.getMinutes().toString().padStart(2, '0');
      const period = hours24 >= 12 ? 'pm' : 'am';
      let hour12 = hours24 % 12;
      hour12 = hour12 === 0 ? 12 : hour12;
      return `${baseDate}  ${hour12}:${minutes} ${period}`;
    }

    return baseDate;
  }

  updateDateDisplay(): void {
    this.generateCalendar();
  }
  // Display helpers for top inputs
  formatDateForInput(date: Date): string {
    if (!date) { return ''; }
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthNames[date.getMonth()];
    const day = date.getDate();
    const year = date.getFullYear();
    return `${month} ${day}, ${year}`;
  }




  onMonthChange(event: Event): void {
    const select = event.target as HTMLSelectElement;
    this.currentMonth = parseInt(select.value, 10);
    this.generateCalendar();
    event.stopPropagation();
  }

  onYearChange(event: Event): void {
    const select = event.target as HTMLSelectElement;
    this.currentYear = parseInt(select.value, 10);
    this.generateCalendar();
    event.stopPropagation();
  }

  // Navigation methods for different views
  navigateToYearView(): void {
    this.currentView = CalendarView.year;
  }

  navigateToMonthView(): void {
    this.currentView = CalendarView.month;
  }

  navigateToDateView(): void {
    this.currentView = CalendarView.day;
    this.generateCalendar();
  }

  // Year selection methods
  onYearClick(year: number, event?: MouseEvent): void {
    
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    
    // Immediate update
    this.currentYear = year;
    this.currentView = CalendarView.month;
  }

  // Month selection methods
  onMonthClick(monthIndex: number, event?: MouseEvent): void {
    
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    
    // Immediate update
    this.currentMonth = monthIndex;
    this.currentView = CalendarView.day;
    this.generateCalendar();
  }

  // Get years in chunks for grid display
  getYearsInRows(): number[][] {
    const years = this.availableYears;
    const rows: number[][] = [];
    for (let i = 0; i < years.length; i += 4) {
      rows.push(years.slice(i, i + 4));
    }
    return rows;
  }

  // Get months in chunks for grid display
  getMonthsInRows(): { name: string; index: number }[][] {
    const monthsData = this.shortMonths.map((name, index) => ({ name, index }));
    const rows: { name: string; index: number }[][] = [];
    for (let i = 0; i < monthsData.length; i += 3) {
      rows.push(monthsData.slice(i, i + 3));
    }
    return rows;
  }

  // Navigation for year view
  navigateYearRange(direction: 'prev' | 'next'): void {
    const rangeSize = this.yearRange.end - this.yearRange.start + 1;
    const increment = direction === 'next' ? rangeSize : -rangeSize;
    this.yearRange.start += increment;
    this.yearRange.end += increment;

    this.availableYears = Array.from(
      { length: this.yearRange.end - this.yearRange.start + 1 },
      (_, i) => this.yearRange.start + i
    );
  }

  // Get current view title
  getCurrentViewTitle(): string {
    switch (this.currentView) {
      case CalendarView.year:
        return `${this.yearRange.start} - ${this.yearRange.end}`;
      case CalendarView.month:
        return this.currentYear.toString();
      case CalendarView.day:
        return `${this.months[this.currentMonth]} ${this.currentYear}`;
      default:
        return '';
    }
  }

  // Check if year is current year
  isCurrentYear(year: number): boolean {
    return year === new Date().getFullYear();
  }

  // Check if month is current month
  isCurrentMonth(monthIndex: number): boolean {
    const now = new Date();
    return monthIndex === now.getMonth() && this.currentYear === now.getFullYear();
  }

  onDateClick(day: CalendarDay, event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();

    if (!day.isSelectable) {
      return;
    }

    if (this.singleDateSelection) {
      this.selectionStart = new Date(day.date);
      this.selectionEnd = new Date(day.date);
      this.isExpectingFromDate = true; // Reset for single date
      
      // Set default times for single date: 00:00 to 23:59
      if (this.enableTimeSelection) {
        this.fromTime = { hour: 0, minute: 0 };
        this.toTime = { hour: 23, minute: 59 };
        this.syncAmPmFromTimes();
      }
      
      this.generateCalendar();
      this.emitSelection();
      // Keep calendar open after single date selection
    } else {
      const clickedDate = new Date(day.date);

      if (this.isExpectingFromDate) {
        // First click or starting a new range - set as "from date"
        this.selectionStart = clickedDate;
        this.selectionEnd = clickedDate; // Set end same as start initially
        this.isExpectingFromDate = false; // Next click will be "to date"
        this.isSelecting = true;
        this.activeTimeSelector = 'from'; // Set active time selector to from
        
        // Set default times for single date: 00:00 to 23:59
        if (this.enableTimeSelection) {
          this.fromTime = { hour: 0, minute: 0 };
          this.toTime = { hour: 23, minute: 59 };
          this.syncAmPmFromTimes();
        }
        
        this.generateCalendar();
        this.emitSelection();
      } else {
        // Second click - set as "to date" and complete the range
        if (clickedDate >= this.selectionStart) {
          this.selectionEnd = clickedDate;
        } else {
          // If clicked date is before start date, swap them
          this.selectionEnd = this.selectionStart;
          this.selectionStart = clickedDate;
        }
        this.isExpectingFromDate = true; // Next click will start a new range
        this.isSelecting = false; // Range is complete
        this.activeTimeSelector = 'to'; // Set active time selector to to
        
        // Set default time for end date: 23:59
        if (this.enableTimeSelection) {
          this.toTime = { hour: 23, minute: 59 };
          this.syncAmPmFromTimes();
        }
        
        this.generateCalendar();
        this.emitSelection();
        // Keep calendar open after range selection is complete
      }
    }
  }

  // Time selection methods
  setActiveTimeSelector(selector: 'from' | 'to', event: MouseEvent): void {
    event.stopPropagation();
    this.activeTimeSelector = selector;
  }

  // Typable time input methods
  onTimeInputKeydown(event: KeyboardEvent, type: 'hour' | 'minute', selector: 'from' | 'to'): void {
    // Allow navigation keys, delete, backspace, numbers
    const allowedKeys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Delete', 'Backspace', 'Tab', 'Enter'];
    const isNumber = /^[0-9]$/.test(event.key);

    if (!isNumber && !allowedKeys.includes(event.key)) {
      event.preventDefault();
    }
  }

  onTimeInputBlur(event: FocusEvent, type: 'hour' | 'minute', selector: 'from' | 'to'): void {
    const input = event.target as HTMLInputElement;
    let value = parseInt(input.value, 10);

    // If empty or invalid, set to current value or 0
    if (isNaN(value) || input.value === '') {
      if (selector === 'from') {
        value = this.fromTime[type];
      } else {
        value = this.toTime[type];
      }
    }

    // Validate ranges
    if (type === 'hour') {
      value = Math.max(0, Math.min(23, value));
    } else {
      value = Math.max(0, Math.min(59, value));
    }

    // Update the display and internal value
    input.value = value.toString().padStart(2, '0');

    if (selector === 'from') {
      this.fromTime[type] = value;
    } else {
      this.toTime[type] = value;
    }

    // Update AM/PM if hour changed in 12h mode
    if (type === 'hour' && this.use12Hours) {
      this.syncAmPmFromTimes();
    }

    // Automatically emit selection when time changes
    if (this.selectionStart && this.selectionEnd) {
      this.emitSelection();
    }
  }

  onTimeInputFocus(event: FocusEvent, type: 'hour' | 'minute', selector: 'from' | 'to'): void {
    const input = event.target as HTMLInputElement;

    // Set current value in the input field when focused
    let currentValue: number;
    if (selector === 'from') {
      currentValue = this.fromTime[type];
    } else {
      currentValue = this.toTime[type];
    }

    input.value = currentValue.toString().padStart(2, '0');

    // Select all text for easy editing
    setTimeout(() => input.select(), 0);
  }

  formatTime(hour: number, minute: number): string {
    return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
  }

  formatTimeWithAmPm(hour: number, minute: number): string {
    const period = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')} ${period}`;
  }

  emitSelection(): void {
    if (!this.selectionStart || !this.selectionEnd) {
      return;
    }

    this.startDate = new Date(this.selectionStart);
    this.endDate = new Date(this.selectionEnd);

    // Apply time selection if enabled
    if (this.enableTimeSelection) {
      this.startDate.setHours(this.fromTime.hour, this.fromTime.minute, 0, 0);
      this.endDate.setHours(this.toTime.hour, this.toTime.minute, 0, 0);
    } else {
      // Default behavior without time selection
      this.startDate = this.getStartOfDay(this.startDate);
      this.endDate = this.singleDateSelection ?
        this.getStartOfDay(this.endDate) :
        this.getEndOfDay(this.endDate);
    }

    const value = this.singleDateSelection ?
      this.startDate :
      { startDate: this.startDate, endDate: this.endDate };

    this.onChange(value);
    this.onTouched();
    this.dateRangeSelected.emit({
      startDate: this.startDate,
      endDate: this.endDate
    });

    this.updateDateDisplay();
  }

  // AM/PM toggle handlers
  onToggleAmPm(selector: 'from' | 'to', value: 'AM' | 'PM', event: MouseEvent): void {
    event.stopPropagation();
    if (!this.use12Hours) { return; }
    const current = selector === 'from' ? this.fromTime : this.toTime;
    const was = this.amPm[selector];
    if (was === value) { return; }
    let hour = current.hour;
    if (value === 'AM' && hour >= 12) {
      hour = hour - 12;
    } else if (value === 'PM' && hour < 12) {
      hour = hour + 12;
    }
    if (selector === 'from') {
      this.fromTime.hour = hour;
      this.amPm.from = value;
    } else {
      this.toTime.hour = hour;
      this.amPm.to = value;
    }
    if (this.selectionStart && this.selectionEnd) {
      this.emitSelection();
    }
  }

  private syncAmPmFromTimes(): void {
    this.amPm.from = this.fromTime.hour >= 12 ? 'PM' : 'AM';
    this.amPm.to = this.toTime.hour >= 12 ? 'PM' : 'AM';
  }

  previousMonth(event: MouseEvent): void {
    event.stopPropagation();

    if (this.currentMonth === 0) {
      this.currentMonth = 11;
      this.currentYear--;
    } else {
      this.currentMonth--;
    }
    this.generateCalendar();
  }

  nextMonth(event: MouseEvent): void {
    event.stopPropagation();

    if (this.currentMonth === 11) {
      this.currentMonth = 0;
      this.currentYear++;
    } else {
      this.currentMonth++;
    }
    this.generateCalendar();
  }



  // Template-accessible methods (must be public)
  isDateInRange(date: Date): boolean {
    if (!this.selectionStart || !this.selectionEnd) {
      return false;
    }

    const dateToCheck = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const startDateCopy = new Date(this.selectionStart.getFullYear(), this.selectionStart.getMonth(), this.selectionStart.getDate());
    const endDateCopy = new Date(this.selectionEnd.getFullYear(), this.selectionEnd.getMonth(), this.selectionEnd.getDate());

    return dateToCheck >= startDateCopy && dateToCheck <= endDateCopy;
  }

  // Footer actions
  onCancel(event: MouseEvent): void {
    event.stopPropagation();

    // Use timeout to ensure form updates and DOM operations complete properly
    setTimeout(() => {
      if (this.backupSelectionStart) {
        this.selectionStart = new Date(this.backupSelectionStart);
      } else {
        this.selectionStart = null;
      }
      if (this.backupSelectionEnd) {
        this.selectionEnd = new Date(this.backupSelectionEnd);
      } else {
        this.selectionEnd = null;
      }
      if (this.backupFromTime) {
        this.fromTime = { ...this.backupFromTime };
      }
      if (this.backupToTime) {
        this.toTime = { ...this.backupToTime };
      }
      if (this.backupAmPm) {
        this.amPm = { ...this.backupAmPm } as { from: 'AM' | 'PM'; to: 'AM' | 'PM' };
      }
      if (this.selectionStart && this.selectionEnd) {
        this.emitSelection();
      }
      this.isOpen = false;
      this.currentView = CalendarView.day;
      this.restoreCalendarPopup();
    }, 0);
  }

  onApply(event: MouseEvent): void {
    event.stopPropagation();

    // Use timeout to ensure form updates and DOM operations complete properly
    setTimeout(() => {
      if (this.selectionStart && this.selectionEnd) {
        this.emitSelection();
      }
      this.isOpen = false;
      this.currentView = CalendarView.day;
      this.restoreCalendarPopup();
    }, 0);
  }

  private initializeYearRange(): void {
    const currentYear = new Date().getFullYear();
    this.yearRange = {
      start: currentYear - 12,
      end: currentYear + 3
    };
    this.availableYears = Array.from(
      { length: this.yearRange.end - this.yearRange.start + 1 },
      (_, i) => this.yearRange.start + i
    );
  }

  private getStartOfDay(date: Date): Date {
    const startOfDay = new Date(date);
    startOfDay.setHours(0, 0, 0, 0);
    return startOfDay;
  }

  private getEndOfDay(date: Date): Date {
    const endOfDay = new Date(date);
    endOfDay.setHours(23, 59, 59, 999);
    return endOfDay;
  }

  private generateCalendar(): void {
    this.calendarDays = [];

    const firstDay = new Date(this.currentYear, this.currentMonth, 1);
    const lastDay = new Date(this.currentYear, this.currentMonth + 1, 0);

    // Make calendar Monday-first
    const jsDay = firstDay.getDay(); // 0=Sun,1=Mon,...
    const dayOfWeek = (jsDay + 6) % 7; // 0=Mon,6=Sun
    const prevMonthLastDay = new Date(this.currentYear, this.currentMonth, 0).getDate();

    // Previous month days
    for (let i = dayOfWeek - 1; i >= 0; i--) {
      const day = prevMonthLastDay - i;
      const date = new Date(this.currentYear, this.currentMonth - 1, day);
      this.calendarDays.push({
        date,
        day,
        inCurrentMonth: false,
        isToday: this.isToday(date),
        isSelected: this.isDateInRange(date),
        isSelectedEnd: this.isDateEndOfRange(date),
        isSelectable: this.isDateSelectable(date)
      });
    }

    // Current month days
    for (let day = 1; day <= lastDay.getDate(); day++) {
      const date = new Date(this.currentYear, this.currentMonth, day);
      this.calendarDays.push({
        date,
        day,
        inCurrentMonth: true,
        isToday: this.isToday(date),
        isSelected: this.isDateInRange(date),
        isSelectedEnd: this.isDateEndOfRange(date),
        isSelectable: this.isDateSelectable(date)
      });
    }

    // Next month days
    const remainingDays = 42 - this.calendarDays.length;
    for (let day = 1; day <= remainingDays; day++) {
      const date = new Date(this.currentYear, this.currentMonth + 1, day);
      this.calendarDays.push({
        date,
        day,
        inCurrentMonth: false,
        isToday: this.isToday(date),
        isSelected: this.isDateInRange(date),
        isSelectedEnd: this.isDateEndOfRange(date),
        isSelectable: this.isDateSelectable(date)
      });
    }
  }

  private isToday(date: Date): boolean {
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  }

  private isDateSelectable(date: Date): boolean {
    if (this.minDate) {
      const minDateCopy = new Date(this.minDate.getFullYear(), this.minDate.getMonth(), this.minDate.getDate());
      if (date < minDateCopy) {
        return false;
      }
    }

    if (this.maxDate) {
      const maxDateCopy = new Date(this.maxDate.getFullYear(), this.maxDate.getMonth(), this.maxDate.getDate());
      if (date > maxDateCopy) {
        return false;
      }
    }

    return true;
  }

  private isDateEndOfRange(date: Date): boolean {
    if (!this.selectionEnd) {
      return false;
    }

    const dateToCheck = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const endDateCopy = new Date(this.selectionEnd.getFullYear(), this.selectionEnd.getMonth(), this.selectionEnd.getDate());

    return dateToCheck.getTime() === endDateCopy.getTime();
  }

  private isSameDate(date1: Date, date2: Date): boolean {
    if (!date1 || !date2) {
      return false;
    }
    return date1.getFullYear() === date2.getFullYear() &&
           date1.getMonth() === date2.getMonth() &&
           date1.getDate() === date2.getDate();
  }

  private parseDateTimeFromText(text: string): Date | null {
    if (!text || typeof text !== 'string') {
      return null;
    }

    const trimmedText = text.trim();

    // Try different date formats
    const datePatterns = [
      // dd-MM-yyyy HH:mm or dd/MM/yyyy HH:mm
      /^(\d{1,2})[-/](\d{1,2})[-/](\d{4})\s+(\d{1,2}):(\d{2})(?:\s*(am|pm))?$/i,
      // MM/dd/yyyy HH:mm or MM-dd-yyyy HH:mm
      /^(\d{1,2})[-/](\d{1,2})[-/](\d{4})\s+(\d{1,2}):(\d{2})(?:\s*(am|pm))?$/i,
      // yyyy-MM-dd HH:mm
      /^(\d{4})-(\d{1,2})-(\d{1,2})\s+(\d{1,2}):(\d{2})(?:\s*(am|pm))?$/i,
      // dd MMM yyyy HH:mm (e.g., "15 Dec 2024 14:30")
      /^(\d{1,2})\s+(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+(\d{4})\s+(\d{1,2}):(\d{2})(?:\s*(am|pm))?$/i,
      // MMM dd, yyyy HH:mm (e.g., "Dec 15, 2024 2:30 pm")
      /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+(\d{1,2}),?\s+(\d{4})\s+(\d{1,2}):(\d{2})(?:\s*(am|pm))?$/i
    ];

    for (const pattern of datePatterns) {
      const match = trimmedText.match(pattern);
      if (match) {
        try {
          let year: number, month: number, day: number, hour: number, minute: number;
          let ampm: string | undefined;

          if (pattern === datePatterns[0] || pattern === datePatterns[1]) {
            // dd-MM-yyyy or MM/dd/yyyy formats - need to detect which one
            const firstNum = parseInt(match[1], 10);
            const secondNum = parseInt(match[2], 10);

            if (firstNum > 12) {
              // dd-MM-yyyy format (day first)
              day = firstNum;
              month = secondNum - 1; // JS months are 0-based
              year = parseInt(match[3], 10);
            } else if (secondNum > 12) {
              // MM/dd/yyyy format (month first)
              month = firstNum - 1;
              day = secondNum;
              year = parseInt(match[3], 10);
            } else {
              // Ambiguous, assume dd-MM-yyyy for international format
              day = firstNum;
              month = secondNum - 1;
              year = parseInt(match[3], 10);
            }
          } else if (pattern === datePatterns[2]) {
            // yyyy-MM-dd format
            year = parseInt(match[1], 10);
            month = parseInt(match[2], 10) - 1;
            day = parseInt(match[3], 10);
          } else if (pattern === datePatterns[3]) {
            // dd MMM yyyy format
            day = parseInt(match[1], 10);
            month = this.getMonthIndex(match[2]);
            year = parseInt(match[3], 10);
          } else if (pattern === datePatterns[4]) {
            // MMM dd, yyyy format
            month = this.getMonthIndex(match[1]);
            day = parseInt(match[2], 10);
            year = parseInt(match[3], 10);
          }

          hour = parseInt(match[match.length - 3], 10);
          minute = parseInt(match[match.length - 2], 10);
          ampm = match[match.length - 1]?.toLowerCase();

          // Handle 12-hour format
          if (ampm) {
            if (ampm === 'am' && hour === 12) {
              hour = 0;
            } else if (ampm === 'pm' && hour !== 12) {
              hour += 12;
            }
          }

          // Validate ranges
          if (month < 0 || month > 11 || day < 1 || day > 31 || hour < 0 || hour > 23 || minute < 0 || minute > 59) {
            continue;
          }

          const parsedDate = new Date(year, month, day, hour, minute);

          // Check if the date is valid (handles invalid dates like Feb 30)
          if (parsedDate.getFullYear() === year &&
              parsedDate.getMonth() === month &&
              parsedDate.getDate() === day &&
              parsedDate.getHours() === hour &&
              parsedDate.getMinutes() === minute) {
            return parsedDate;
          }
        } catch (error) {
          // Continue to next pattern if parsing fails
          continue;
        }
      }
    }

    // Try native Date parsing as fallback
    const fallbackDate = new Date(trimmedText);
    if (!isNaN(fallbackDate.getTime()) && fallbackDate.getFullYear() > 1900) {
      return fallbackDate;
    }

    return null;
  }

  private getMonthIndex(monthName: string): number {
    const months = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
    return months.indexOf(monthName.toLowerCase());
  }

  private setDateFromParsedValue(parsedDate: Date): void {
    // Set the date selection
    this.selectionStart = new Date(parsedDate);
    if (this.singleDateSelection) {
      this.selectionEnd = new Date(parsedDate);
    } else {
      // For range selection, set end date to same as start initially
      this.selectionEnd = new Date(parsedDate);
    }

    // Set the time values
    if (this.enableTimeSelection) {
      this.fromTime = {
        hour: parsedDate.getHours(),
        minute: parsedDate.getMinutes()
      };

      if (this.singleDateSelection) {
        this.toTime = {
          hour: parsedDate.getHours(),
          minute: parsedDate.getMinutes()
        };
      } else {
        // For range, set end time to end of day by default
        this.toTime = { hour: 23, minute: 59 };
      }

      // Update AM/PM if using 12-hour format
      if (this.use12Hours) {
        this.amPm.from = this.fromTime.hour >= 12 ? 'PM' : 'AM';
        this.amPm.to = this.toTime.hour >= 12 ? 'PM' : 'AM';
      }
    }

    // Update startDate and endDate
    this.startDate = new Date(this.selectionStart);
    this.endDate = new Date(this.selectionEnd);

    if (this.enableTimeSelection) {
      this.startDate.setHours(this.fromTime.hour, this.fromTime.minute, 0, 0);
      this.endDate.setHours(this.toTime.hour, this.toTime.minute, 0, 0);
    }

    this.updateDateDisplay();
  }
}
